import _ from "lodash";
import React from "react";
import classNames from "classnames";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import BenchmarkView from "UIComponents/BenchmarkView";
import EmptyField from "UIComponents/EmptyField";
import { graphql, compose } from "lib/apolloClient";
import classes from "./MYPObjectiveRubric.scss";
import { connect } from "react-redux";
import { DEMO_MYP_ORG } from "Constants";
import {
  generateNestedSelectedData,
  getUniqAssociatedParentsOfNodes,
  mergeAssociatedParentsWithNodes,
  getPYPElementSetFromNodes,
} from "Utils";
import RubricTable from "./RubricTable";
import RubricTableV2 from "./RubricTableV2";
import RubricTableWithComment from "./RubricTableWithComment";
import { plannerElementValue } from "modules/ElementValueUtils";
import { handledMYPRubricDescriptors } from "modules/AssessmentTool/AssessmentToolModule";
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";
import {
  getLearningGoalsDataMemoize,
  getSubjectsBasedOnUnitSelection,
  getSubjectRecommendedPhaseMap,
} from "Assessments/modules/Utils";
import {
  getPlannerElementSetsOfCurriculumEnhancer,
  getMYPObjectivesAndAchievementLevelsEnhancer,
} from "Assessments/modules/AssessmentQueryEnhancers";
import {
  ELEMENT_TYPE_MYP_OBJECTIVES,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  ELEMENT_SUB_TYPE_INTERDISCIPLINARY_CRITERIA,
  ASSESSMENT,
  STUDENT_ASSIGNMENT,
  ELEMENT_TYPE_MYP_ACHIEVEMENT_LEVEL,
} from "Constants/stringConstants";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import TreeView from "UnitPlans/components/IBcomponents/TreeView";
import {
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
  getCurrentAcademicYear,
} from "modules/CommonSelectors";
import { getCourseDetailsFromCache } from "Teacher/routes/Courses/routes/Course/modules/CourseGraphqlHelpers";
import { getRecommendedPhaseOfSubjectsQuery } from "Assessments/modules/AssessmentQuery";
import ACLStore from "lib/aclStore";
import { ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION } from "Constants/permissionConstants";

const updateObjectiveNodes = ({ nodes }) => {
  return _.map(nodes, node => {
    const { label, subText } = node;
    return {
      ...node,
      label: subText || label,
    };
  });
};

const updateObjectiveNodesMemoize = _.memoize(
  params => updateObjectiveNodes(params),
  params => JSON.stringify(params)
);

const _getNestedObjectives = ({ objectives, parentType }) => {
  const objectiveNodes = updateObjectiveNodesMemoize({
    nodes: _.sortBy(_.orderBy(objectives, ["displaySequence"]), ["parent"]),
  });

  let associatedParentNodes = getUniqAssociatedParentsOfNodes({
    nodes: objectiveNodes,
  });

  // associatedParentNodes will contain subjects nodes
  // if block below will update the label to include objective name, for example - "English - Year 1 objectives"
  if (
    !_.isEmpty(associatedParentNodes) &&
    _.includes([ASSESSMENT, STUDENT_ASSIGNMENT], parentType)
  ) {
    associatedParentNodes = _.map(associatedParentNodes, node => {
      const { children, label } = node;

      const childrenData = _.find(objectives, objective => {
        return _.includes(children, objective?.id);
      });

      let childLabel = _.get(childrenData, "label", "");

      if (childLabel) {
        if (_.includes(childLabel, "-")) {
          childLabel = _.replace(childLabel, "-", ":");
        } else {
          childLabel = `: ${childLabel}`;
        }

        return {
          ...node,
          label: `${label} ${childLabel}`,
        };
      }
      return node;
    });
  }

  const value = _.map(objectiveNodes, item => item?.id);

  let updatedObjectives = objectiveNodes;
  if (_.size(associatedParentNodes) > 0) {
    updatedObjectives = mergeAssociatedParentsWithNodes({
      associatedParentNodes,
      nodes: updatedObjectives,
    });
  }

  return generateNestedSelectedData({
    ...getPYPElementSetFromNodes({ nodes: updatedObjectives }),
    value,
  });
};

const getNestedObjectives = _.memoize(
  params => _getNestedObjectives(params),
  params => JSON.stringify(params)
);

class MYPObjectiveRubric extends React.PureComponent {
  constructor(props) {
    super(props);
    this.rubricTableRef = null;
    if (props.customRef) {
      props.customRef(this);
    }
  }

  componentDidMount = () => {
    const { isCreateMode = false, objectiveField = {} } = this.props;

    /* When we create a MYP Objective rubric and select some criteria's those criteria's gets
    stored in Assessment -> allFields -> objectivesMYP -> value (i.e value: [1234,5678] if 2 criteria's are selected)
    and when we remove MYP Objective rubric these objectiveMYP's value does not get removed along with the rubric
    so if user creates a MYP Objective rubric again in the same assessment these criteria's ([1234,5667]) will be selected 
    by default because they were never removed. These criteria's were stored in myp_objective_descriptors along the with 
    rubric id (fk_rubric_id) (as these criteria's were created with old rubric, fk_rubric_id will be of old rubric which 
    we already removed) and descriptors with new rubric id were never created hence it created two issues
    1. you can not edit rubric table cells as there is no descriptor id
    2. you can not evaluate the rubric because of same reason (there is no descriptor id)

    to solve this problem, while creating MYP Objective we check if objectiveMYP value is not null  (which  
    means that myp objective rubric was created before, some criteria's were selected and then rubric
    was deleted), if value is not null then we call the createMypObjectiveRubricDescriptorsForObjectiveMutation
    mutation to update the descriptors with new rubric id  */
    const { value } = objectiveField;
    if (isCreateMode && !_.isEmpty(value)) {
      this.updateObjectives(value, []);
    }
  };

  blur = () => {
    if (this.rubricTableRef) {
      this.rubricTableRef.blur();
    }
  };

  updateObjectives = (newValues, oldValues) => {
    const {
      objectiveField = {},
      updateInputField,
      handledMYPRubricDescriptors,
      assessmentTool,
      objectivesData,
      isObjectivesSelectionV2,
      portalType,
    } = this.props;

    const { value, fieldKey, unitPlanFieldResolvedValue } = objectiveField;

    const updatedNodes = isObjectivesSelectionV2
      ? _.get(objectivesData, "globalData.nodes", [])
      : _.get(unitPlanFieldResolvedValue, "nodes", []);

    updateInputField({ [fieldKey]: newValues }, { nodes: updatedNodes });

    handledMYPRubricDescriptors({
      newObjectives: newValues,
      oldObjectives: oldValues ? oldValues : value,
      rubricId: assessmentTool.id,
      portalType,
    });
  };

  getObjectiveSelectionV2 = () => {
    const {
      showOnlyUnitSelections,
      objectivesData,
      options,
      t,
      objectives,
      parentType,
      assignmentId,
      isInterdisciplinaryRubric,
      courseDetails,
      isGeneratingAIResponse,
    } = this.props;

    const nestedObjectives = getNestedObjectives({
      objectives,
      parentType,
    });

    const fieldKey = _.get(options, "objectiveField.fieldKey", "");
    const fieldLabel = isInterdisciplinaryRubric
      ? t("common:interdisciplinary_criteria")
      : t("common:myp_criteria");

    const customTableHeaderElement = (
      <div className={classes.criteriaHeaderContainer}>
        <div className="text-label-1">
          {t("classRoom:select_criteria_to_evaluate_on")}
        </div>
        <div className="text-body-2">
          {t("classRoom:rubric_will_be_auto_generated_based_on_criteria")}
        </div>
      </div>
    );

    const customBottomElement = !_.isEmpty(objectives) ? (
      <RubricTable
        {...this.props}
        customRef={ref => (this.rubricTableRef = ref)}
        nestedObjectives={nestedObjectives}
        assignmentId={assignmentId}
        isGeneratingAIResponse={isGeneratingAIResponse}
      />
    ) : null;

    return (
      <TreeView
        {...objectivesData}
        fieldKey={fieldKey}
        fieldLabel={fieldLabel}
        showSearchFilter={false}
        showSideNavigation={!isInterdisciplinaryRubric}
        showRadioButton={!isInterdisciplinaryRubric}
        showAllLearningGoals={!showOnlyUnitSelections}
        isPlannerElement={true}
        useSubText={true}
        updateInputField={this.updateObjectives}
        customTableHeaderElement={customTableHeaderElement}
        customBottomElement={customBottomElement}
        courseDetails={courseDetails}
      />
    );
  };

  getObjectiveSelection = () => {
    const { mode, objectiveField = {}, t } = this.props;
    const { value, unitPlanFieldResolvedValue } = objectiveField;

    const objectivesNodes = _.get(objectiveField, "resolvedValue.nodes", []);

    let nodes = _.uniqBy(
      [...unitPlanFieldResolvedValue.nodes, ...objectivesNodes],
      "id"
    );

    const hasDisplaySequence = !_.some(nodes, node => !node.displaySequence);

    if (hasDisplaySequence) {
      nodes = _.orderBy(nodes, ["displaySequence"], ["asc"]);
    }

    const updatedUnitPlanFieldResolvedValue = {
      ...unitPlanFieldResolvedValue,
      nodes: updateObjectiveNodesMemoize({
        nodes,
      }),
    };

    const nestedValue = plannerElementValue({
      fieldObj: {
        viewType: "Nestedview",
      },
      resolvedValue: updatedUnitPlanFieldResolvedValue,
      value: _.map(nodes, item => item.id),
    });

    return (
      <React.Fragment>
        <div className={classes.subText}>
          {t("common:a_rubric_will_be_generated")}
        </div>
        <BenchmarkView
          mode={"view"}
          value={nestedValue || {}}
          selectedValues={value || []}
          isTagging={mode == "edit"}
          updateInputField={this.updateObjectives}
        />
      </React.Fragment>
    );
  };

  render() {
    const {
      objectives,
      unitPlanObjectives,
      subType,
      emptyTextObj,
      mode,
      t,
      isSplitView = false,
      isObjectivesSelection,
      isObjectivesSelectionV2,
      isMYPObjectiveRubricNewUI,
      parentType,
      showCommentForRating,
      assignmentId,
    } = this.props;

    const nestedObjectives = getNestedObjectives({
      objectives,
      parentType,
    });

    const Rubric = showCommentForRating
      ? RubricTableWithComment
      : isSplitView
      ? RubricTableV2
      : RubricTable;

    const containerClasses = classNames({
      [classes.container]: !isObjectivesSelectionV2,
      [classes.containerV2]: isObjectivesSelectionV2,
    });

    const innerContainerClasses = classNames({
      [classes.innerContainer]: isObjectivesSelectionV2,
    });

    return (
      <div className={containerClasses}>
        {_.isEmpty(unitPlanObjectives) &&
        mode == "edit" &&
        !isMYPObjectiveRubricNewUI ? (
          <EmptyField
            title={t(_.get(emptyTextObj, subType, "common:not_defined"))}
          />
        ) : (
          <div className={innerContainerClasses}>
            {isObjectivesSelectionV2
              ? this.getObjectiveSelectionV2()
              : isObjectivesSelection
              ? this.getObjectiveSelection()
              : null}
            {!_.isEmpty(objectives) && !isObjectivesSelectionV2 ? (
              <Rubric
                {...this.props}
                customRef={ref => (this.rubricTableRef = ref)}
                nestedObjectives={nestedObjectives}
                assignmentId={assignmentId}
              />
            ) : null}
          </div>
        )}
      </div>
    );
  }
}

const MYPObjectiveRubricWrapper = props => {
  const {
    unitPlanFields,
    assessmentFields,
    plannerElements = [],
    options,
    curriculumType,
    mode,
    showObjectiveSelection = true,
    isMYPObjectiveRubricNewUI = false,
    parentType,
    courseId,
    subType,
    isFromQuestion = false,
    subjectRecommendedPhaseMap,
    learningCourseId,
  } = props;

  const plannerElementNodes = _.get(_.head(plannerElements), "nodes", []);
  const fieldKey = _.get(options, "objectiveField.fieldKey", "");

  const objectivesData = getLearningGoalsDataMemoize({
    unitPlanFields,
    assessmentFields,
    fieldKey,
    plannerElementType: ELEMENT_TYPE_MYP_OBJECTIVES,
    curriculumType,
    plannerElementNodes,
    courseId,
    plannerElementSubType: subType,
    isPlannerElement: true,
    subjectRecommendedPhaseMap,
    learningCourseId,
  });

  const isObjectivesSelection = mode == "edit" && showObjectiveSelection;
  const isObjectivesSelectionV2 =
    isObjectivesSelection &&
    isMYPObjectiveRubricNewUI &&
    _.includes([ASSESSMENT, "QUESTION"], parentType);

  return (
    <MYPObjectiveRubric
      {...props}
      objectivesData={objectivesData}
      isObjectivesSelection={isObjectivesSelection}
      isObjectivesSelectionV2={isObjectivesSelectionV2}
    />
  );
};

const mapActionCreators = {
  handledMYPRubricDescriptors,
  createAssessmentTool,
};

const mapStateToProps = (state, ownProps) => {
  const curriculumType = "IB_MYP";

  let curriculumId = getSelectedCurriculumId(state);

  const isDemo = _.get(state, "teacher.selected_class.isDemo", false);
  //Can move it into template
  const elementTypes = [ELEMENT_TYPE_MYP_ACHIEVEMENT_LEVEL];

  let curriculum = null;

  if (curriculumId) {
    curriculum = _.find(state.platform.organizationCurriculumPrograms, {
      id: curriculumId,
    });
  } else {
    curriculum = _.find(state.platform.organizationCurriculumPrograms, {
      type: curriculumType,
    });
  }

  const unitPlanObjectives = _.get(
    ownProps,
    "options.objectiveField.unitPlanFieldValue",
    []
  );

  const {
    assessmentFields,
    toolType,
    isMYPObjectiveRubricNewUI,
    unitPlanFields,
    parentId,
    parentType,
    learningCourseId,
  } = ownProps;
  let { portalType } = ownProps;

  const isInterdisciplinaryRubric =
    toolType === MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC;
  const subType = isInterdisciplinaryRubric
    ? ELEMENT_SUB_TYPE_INTERDISCIPLINARY_CRITERIA
    : null;

  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course"
  );
  const courseId = ownProps?.selectedCourseId || selectedCourseId;

  const resolvedSubjects = getSubjectsBasedOnUnitSelection({
    unitPlanFields,
    assessmentFields,
    courseId,
    learningCourseId,
  });
  const subjectIds = _.map(resolvedSubjects, ({ id }) => id);
  let courseDetails = {};
  if (courseId) {
    courseDetails = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters: { academicYearId: getSelectedAcademicYearId() },
    });
  }

  const isToddleLibrary = state.login.activeTab == "TODDLE_LIBRARY";
  if (isToddleLibrary && parentType == ASSESSMENT && parentId) {
    const organizationId = _.get(state, "login.userInfo.org_id");
    const academicYearId = _.get(
      getCurrentAcademicYear({ organizationId }),
      "id",
      null
    );

    const collaboratorsFilter = {
      userAcademicYearId: academicYearId,
    };

    const assessmentDetails = getAssessmentDetailsFromCache({
      id: parentId,
      collaboratorsFilter,
    });
    const curriculumProgram = _.get(assessmentDetails, "curriculumProgram", {});
    curriculumId = _.get(curriculumProgram, "id", "");
    if (_.isEmpty(portalType)) portalType = "COMMUNITY";
  } else if (isDemo) {
    curriculumId = _.get(DEMO_MYP_ORG, "curriculumId", "");
  } else {
    curriculumId = _.get(curriculum, "id", "");
  }

  const assessmentSubjectIds = _.map(resolvedSubjects, "id");

  const isPhaseRecommendationEnabled = ACLStore.can(
    ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION
  );

  return {
    curriculumType,
    curriculumNodeInterfaceType: _.get(curriculum, "nodeInterfaceType", ""),
    curriculumId,
    elementTypes,
    isData: true,
    isLoading: false,
    assessmentFields,
    unitPlanObjectives,
    plannerElementTypes: isMYPObjectiveRubricNewUI
      ? [ELEMENT_TYPE_MYP_OBJECTIVES]
      : [],
    isStepWithPlannerElement: true,
    filters: !isInterdisciplinaryRubric
      ? { associatedParents: [{ ids: subjectIds, type: "SUBJECT" }] }
      : { subTypes: [ELEMENT_SUB_TYPE_INTERDISCIPLINARY_CRITERIA] },
    isInterdisciplinaryRubric,
    subType,
    courseId,
    portalType,
    courseDetails,
    assessmentSubjectIds,
    isPhaseRecommendationEnabled,
  };
};

export default compose(
  I18nHOC({ resource: ["unitPlan", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  getMYPObjectivesAndAchievementLevelsEnhancer,
  getPlannerElementSetsOfCurriculumEnhancer,
  graphql(getRecommendedPhaseOfSubjectsQuery, {
    name: "getRecommendedPhaseOfSubjects",
    skip: ({ isPhaseRecommendationEnabled, assessmentSubjectIds, mode }) =>
      !isPhaseRecommendationEnabled ||
      _.isEmpty(assessmentSubjectIds) ||
      mode === "view",
    options: ({ assessmentSubjectIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ids: assessmentSubjectIds,
      },
    }),
    props({ getRecommendedPhaseOfSubjects, ownProps: { isData, isLoading } }) {
      const data = getRecommendedPhaseOfSubjects.multiNode;
      const subjectRecommendedPhaseMap = getSubjectRecommendedPhaseMap({
        subjects: data,
      });

      return {
        subjectRecommendedPhaseMap,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getRecommendedPhaseOfSubjects["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  withLoader
)(MYPObjectiveRubricWrapper);

MYPObjectiveRubricWrapper.defaultProps = {
  isEvaluation: true,
};

//Need Review @kunal
